import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { CARD_BG, CARD_BORDER, EASE_OUT, MUTED_GRAY, SHIELD_CYAN, TEXT_LIGHT } from './constants';

/* ─── FadeSection ─────────────────────────────────────────── */

interface FadeSectionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

export function FadeSection({ children, className = '', delay = 0 }: FadeSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} transition={{ duration: 0.5, delay, ease: EASE_OUT }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── GlowCard ────────────────────────────────────────────── */

interface GlowCardProps {
  children: React.ReactNode;
  className?: string;
  glowColor?: string;
  delay?: number;
}

export function GlowCard({ children, className = '', glowColor = SHIELD_CYAN, delay = 0 }: GlowCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }} transition={{ duration: 0.5, delay, ease: EASE_OUT }} className={`relative rounded-xl p-px ${className}`} style={{ background: `linear-gradient(135deg, ${glowColor}40, ${CARD_BORDER}, ${glowColor}20)` }}>
      <div className='h-full rounded-xl p-5 md:p-6' style={{ backgroundColor: CARD_BG }}>
        {children}
      </div>
    </motion.div>
  );
}

/* ─── Section ─────────────────────────────────────────────── */

interface SectionProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  bg?: React.ReactNode;
}

export function Section({ children, className = '', id, bg }: SectionProps) {
  return (
    <section id={id} className={`relative overflow-hidden px-4 py-20 md:px-8 md:py-28 ${className}`} style={{ zIndex: 1 }}>
      {bg}
      <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
        {children}
      </div>
    </section>
  );
}

/* ─── SectionTitle ────────────────────────────────────────── */

interface SectionTitleProps {
  subtitle: string;
  title: string;
  description?: string;
  accentColor?: string;
}

export function SectionTitle({ subtitle, title, description, accentColor = SHIELD_CYAN }: SectionTitleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.5, ease: EASE_OUT }} className='mb-12 text-center md:mb-16'>
      <span className='mb-3 block text-xs font-semibold uppercase tracking-[0.3em] md:text-sm' style={{ color: accentColor, fontFamily: 'var(--font-outfit)' }}>
        {subtitle}
      </span>
      <h2 className='mb-4 text-3xl font-bold md:text-4xl lg:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
        {title}
      </h2>
      {description && (
        <p className='mx-auto max-w-2xl text-base leading-relaxed md:text-lg' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
          {description}
        </p>
      )}
    </motion.div>
  );
}
